import dayjs from "dayjs";
import styles from "../MoveOutChecklist.module.css";
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { PhaseBanner } from "./CommonComponents";
import RentTypeSwitch from "./RentTypeSwitch";

const currency = (value) =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(Math.abs(parseFloat(value) || 0));

const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const thStyle = { textAlign: "left", padding: "6px 0", fontSize: 11, fontWeight: 600, color: "#6B7280" };
const thRight = { ...thStyle, textAlign: "right" };
const rowStyle = { borderBottom: "1px solid #E5E7EB" };
const tdRight = { padding: "8px 0", textAlign: "right", fontVariantNumeric: "tabular-nums" };
const noteStyle = { fontSize: 12, color: "#6B7280", marginBottom: 10 };

/*
 * All amounts come from the backend statement (v1/moveout-payment-data), which is the
 * same calculation that gets posted to the ledger, PDF and email on submit.
 * Sign convention: positive = tenant owes, negative = refund to tenant.
 */
const StatementTab = ({
    moveoutData,
    submitMoveoutChceckList,
    setTab
}) => {
    const { getAccessTokenSilently } = useAuth0();
    const [rentType, setRentType] = useState(moveoutData?.final_month_rent_type || "prorated");
    const [statement, setStatement] = useState(null);
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
        const fetchStatement = async () => {
            setFetching(true);
            try {
                const token = await getAccessTokenSilently({
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE
                });

                const response = await axios.get(
                    process.env.REACT_APP_PAYMENT_API_URL +
                    "v1/moveout-payment-data/" +
                    moveoutData?.tenant_id,
                    {
                        params: { rent_type: rentType },
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    }
                );

                setStatement(response.data.data);
            } catch (error) {
                console.error("Failed to fetch move-out statement:", error);
            }
            setFetching(false);
        };

        if (moveoutData?.tenant_id) {
            fetchStatement();
        }
    }, [moveoutData?.tenant_id, rentType, getAccessTokenSilently]);

    const finalBalance = parseFloat(statement?.final_balance) || 0;
    const isRefund = finalBalance < 0;
    const isDue = finalBalance > 0;
    const isProrated = rentType === "prorated" && !statement?.is_full_stay;
    const voidedItems = statement?.voided_items || [];
    const monthLabel = dayjs(moveoutData?.moveout_date).format("MMM YYYY");

    const onSubmit = () => {
        submitMoveoutChceckList({
            is_submitted: 1,
            is_completed: 1,
            current_step: 4,
            final_month_rent_type: statement?.is_full_stay ? "full" : rentType,
            final_amount: finalBalance
        });
    };

    // Positive values are charges to the tenant, negative values are credits
    const signedRow = (label, value) => {
        const v = parseFloat(value) || 0;
        const isCharge = v > 0;
        return (
            <div className={styles.sr}>
                <span className={styles.sk}>{label}</span>
                <span className={`${styles.sv} ${v === 0 ? "" : isCharge ? styles.charge : styles.credit}`}>
                    {v === 0 ? currency(0) : `${isCharge ? "-" : "+"}${currency(v)}`}
                </span>
            </div>
        );
    };

    return (
        <>
            <div className={`${styles.tp} ${styles.tpActive}`}>

                <PhaseBanner
                    type="green"
                    icon="receipt"
                    title="Final Statement — Ready to send"
                    sub={`Inspection on ${dayjs(moveoutData?.inspection_date).format("MMM DD, YYYY")}. Review the settlement and send the final statement to the tenant.`}
                />

                {/* =========================
                    RENT ADJUSTMENT / PAYMENT RECONCILIATION
                ========================= */}
                <div className={styles.sc}>
                    <div className={styles.scTitle}>
                        Rent adjustment — {monthLabel}
                    </div>

                    <RentTypeSwitch
                        value={rentType}
                        onChange={setRentType}
                        statement={statement}
                        disabled={fetching}
                    />

                    <div className={styles.sr}>
                        <span className={styles.sk}>Move-out date</span>
                        <span className={styles.sv}>{dayjs(moveoutData?.moveout_date).format("MMM DD, YYYY")}</span>
                    </div>

                    <div className={styles.sr}>
                        <span className={styles.sk}>Days occupied in month</span>
                        <span className={styles.sv}>
                            {statement?.days_occupied ?? dayjs(moveoutData?.moveout_date).date()} of{" "}
                            {statement?.days_in_month ?? dayjs(moveoutData?.moveout_date).daysInMonth()} days
                        </span>
                    </div>

                    {statement && (
                        <>
                            <div className={styles.sr}>
                                <span className={styles.sk}>Monthly rent</span>
                                <span className={styles.sv}>{currency(statement.monthly_rent)}</span>
                            </div>

                            <div className={styles.sr}>
                                <span className={styles.sk}>
                                    {isProrated
                                        ? `Prorated rent (${statement.days_occupied} days × ${currency(statement.daily_rate)}/day)`
                                        : "Full month rent"}
                                </span>
                                <span className={styles.sv}>{currency(statement.final_month_charge)}</span>
                            </div>

                            <div className={styles.sr}>
                                <span className={styles.sk}>Payments received for {monthLabel}</span>
                                <span className={`${styles.sv} ${styles.credit}`}>{currency(statement.final_month_paid)}</span>
                            </div>

                            {signedRow(
                                statement.final_month_net >= 0 ? `Net due for ${monthLabel}` : `Net credit for ${monthLabel}`,
                                statement.final_month_net
                            )}
                        </>
                    )}
                </div>

                {/* =========================
                    VOIDED FUTURE BILLS
                ========================= */}
                {voidedItems.length > 0 && (
                    <div className={styles.sc}>
                        <div className={styles.scTitle}>Voided future bills</div>

                        <div style={noteStyle}>
                            These bills are dated after the move-out date and will be voided. They are not included in the previous balance or the final balance.
                        </div>

                        <table style={tableStyle}>
                            <thead>
                                <tr style={rowStyle}>
                                    <th style={thStyle}>Bill</th>
                                    <th style={thStyle}>Date</th>
                                    <th style={thRight}>Paid</th>
                                    <th style={thRight}>Amount</th>
                                </tr>
                            </thead>

                            <tbody>
                                {voidedItems.map((item, index) => (
                                    <tr key={item?.id || index} style={rowStyle}>
                                        <td style={{ padding: "8px 0" }}>
                                            <div style={{ fontWeight: 500 }}>{item?.description || "Bill"}</div>
                                            {item?.payment_type && (
                                                <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{item.payment_type}</div>
                                            )}
                                        </td>
                                        <td style={{ padding: "8px 0", whiteSpace: "nowrap" }}>
                                            {item?.date ? dayjs(item.date).format("MMM DD, YYYY") : "—"}
                                        </td>
                                        <td style={tdRight}>{item?.paid_amount > 0 ? currency(item.paid_amount) : "—"}</td>
                                        <td style={tdRight}>{currency(item?.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className={styles.sr} style={{ marginTop: 8, paddingTop: 10 }}>
                            <span className={styles.sk}>Total voided (not charged)</span>
                            <span className={styles.sv}>{currency(statement?.void_total)}</span>
                        </div>
                    </div>
                )}

                {/* =========================
                    DAMAGE DETAIL
                ========================= */}
                {statement?.damages?.length > 0 && (
                    <div className={styles.sc}>
                        <div className={styles.scTitle}>Damage detail</div>

                        <table style={tableStyle}>
                            <thead>
                                <tr style={rowStyle}>
                                    <th style={thStyle}>Item</th>
                                    <th style={thRight}>Cost</th>
                                </tr>
                            </thead>

                            <tbody>
                                {statement.damages.map((d, i) => (
                                    <tr key={i} style={rowStyle}>
                                        <td style={{ padding: "7px 0" }}>{d?.description || "—"}</td>
                                        <td style={tdRight}>{currency(d?.cost)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className={styles.sr}>
                            <span className={styles.sk}>Total damage charges</span>
                            <span className={`${styles.sv} ${styles.charge}`} style={{ fontSize: 16 }}>
                                -{currency(statement.damage_total)}
                            </span>
                        </div>
                    </div>
                )}

                {/* =========================
                    SETTLEMENT
                ========================= */}
                {statement && (
                    <div className={styles.sc}>
                        <div className={styles.scTitle}>Deposit settlement</div>

                        {signedRow(
                            statement.prior_balance >= 0 ? "Previous outstanding balance" : "Previous credit balance",
                            statement.prior_balance
                        )}

                        {signedRow(
                            statement.final_month_net >= 0 ? `${monthLabel} rent due` : `${monthLabel} rent credit`,
                            statement.final_month_net
                        )}

                        {signedRow("Security deposit refund", -statement.security_deposit)}

                        {statement.damage_total > 0 && signedRow("Damage charges (tenant)", statement.damage_total)}

                        <div className={styles.sr}>
                            <span className={styles.sk}>
                                {isRefund ? "Refund to tenant" : isDue ? "Balance due from tenant" : "Account settled"}
                            </span>
                            <span
                                className={`${styles.sv} ${isRefund ? styles.credit : isDue ? styles.charge : ""}`}
                                style={{ fontSize: 16 }}
                            >
                                {currency(finalBalance)}
                            </span>
                        </div>
                    </div>
                )}

                {/* =========================
                    VERDICT
                ========================= */}
                {statement && (
                    <div className={`${styles.verdict} ${isDue ? styles.verdictOwe : styles.verdictRefund}`}>
                        <i className={`ti ${isDue ? "ti-alert-circle" : "ti-circle-check"} ${styles.verdictIcon}`} />

                        <div>
                            <div className={styles.vt}>
                                {isRefund
                                    ? `Refund of ${currency(finalBalance)}`
                                    : isDue
                                        ? `Collect balance of ${currency(finalBalance)}`
                                        : "Account settled"}
                            </div>

                            <div className={styles.vs}>
                                {isRefund
                                    ? "Post credit to tenant ledger. Transfer refund within 30 days of move-out."
                                    : isDue
                                        ? "Raise final invoice. Collect before releasing deposit remainder."
                                        : "No further payment or refund is required."}
                            </div>
                        </div>
                    </div>
                )}

                {/* =========================
                    ACTIONS
                ========================= */}
                <div className={styles.fa}>
                    <button
                        className={`${styles.btn} ${styles.btnS}`}
                        onClick={() => {
                            setTab((p) => p - 1);
                        }}
                    >
                        <i className="ti ti-arrow-left" /> Back
                    </button>

                    <div className={styles.faRight}>
                        <button className={`${styles.btn} ${styles.btnS}`}>
                            Cancel
                        </button>

                        <button
                            className={`${styles.btn} ${styles.btnP}`}
                            onClick={onSubmit}
                            disabled={!statement || fetching}
                        >
                            <i className="ti ti-send" /> Send final statement
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default StatementTab;
